import { formatDistanceToNow, isValid, parseISO } from 'date-fns';

export const ALL_COUNTRIES_VALUE = 'all';

/** Build select options from API country lists with an "All countries" sentinel first. */
export function countryOptionsFromList(countries, allLabel = 'All countries') {
  const list = (Array.isArray(countries) ? countries : [])
    .map((c) => String(c).trim())
    .filter(Boolean);
  const unique = [...new Set(list)].sort((a, b) => a.localeCompare(b));
  return [
    { value: ALL_COUNTRIES_VALUE, label: allLabel },
    ...unique.map((c) => ({ value: c, label: c })),
  ];
}

export function countryFilterToParams(country) {
  const params = {};
  if (country && country !== ALL_COUNTRIES_VALUE) params.country = country;
  return params;
}

export function impactBadgeClass(impact) {
  return IMPACT_CHIP_STYLES[impact] || IMPACT_CHIP_STYLES['Context only'];
}

export const IMPACT_CHIP_STYLES = {
  Unfavourable: 'bg-red-50 text-red-700 ring-1 ring-red-200',
  Favourable: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  Mixed: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  'Context only': 'bg-gray-50 text-gray-600 ring-1 ring-gray-200',
};

export const LABEL_CHIP_STYLES = {
  High: 'bg-red-50 text-red-700 ring-1 ring-red-200',
  Elevated: 'bg-orange-50 text-orange-700 ring-1 ring-orange-200',
  Watch: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  Low: 'bg-gray-50 text-gray-600 ring-1 ring-gray-200',
  'No evidence': 'bg-gray-50 text-gray-500 ring-1 ring-gray-200',
};

export function labelBadgeClass(label) {
  return LABEL_CHIP_STYLES[label] || LABEL_CHIP_STYLES['No evidence'];
}

/** Bar fill color by proximity score (0–100). */
export function scoreBarFillClass(score) {
  const n = Number(score) || 0;
  if (n === 0) return 'bg-gray-300';
  if (n <= 24) return 'bg-gray-400';
  if (n <= 49) return 'bg-yellow-400';
  if (n <= 74) return 'bg-orange-500';
  return 'bg-red-500';
}

/** Score number text color. */
export function scoreTextClass(score) {
  const n = Number(score) || 0;
  if (n === 0) return 'text-gray-400';
  return 'text-[#0A1A2F]';
}

/** Sparkline bar fill — colour by score value. */
export function colorForScore(v) {
  const n = Number(v) || 0;
  if (n === 0) return '#d1d5db';
  if (n < 25) return '#9ca3af';
  if (n < 50) return '#eab308';
  if (n < 75) return '#f97316';
  return '#ef4444';
}

export function formatRelativeDate(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = parseISO(dateStr);
    if (!isValid(d)) return '—';
    // A "last refreshed" is a past event. Clock skew of a few seconds between
    // the API host and the browser otherwise renders it as "in 1 minute".
    if (d.getTime() > Date.now()) return 'just now';
    return formatDistanceToNow(d, { addSuffix: true });
  } catch {
    return '—';
  }
}

export function pluralizeCount(n, word) {
  const count = Number(n) || 0;
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

export function truncateText(text, max = 80) {
  if (!text) return '';
  const s = String(text).trim();
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

export function computeSummaryCards({ accountBarometer, personaBarometer, competitorView, marketSummary }) {
  const accountRows = accountBarometer?.rows || [];
  const personaRows = personaBarometer?.rows || [];
  const competitorRows = competitorView?.rows || [];

  const totalSignals = accountRows.reduce((sum, r) => sum + (Number(r.signal_count) || 0), 0);
  const newSignals = competitorRows.reduce(
    (sum, r) => sum + (Number(r.new_signals_this_window) || 0),
    0,
  );
  const highProximity = accountRows.filter((r) => r.label === 'High').length;
  const personasWithLinks = personaRows.length;
  const favourable = accountRows.filter((r) => r.impact === 'Favourable').length;
  const unfavourable = accountRows.filter((r) => r.impact === 'Unfavourable').length;

  return {
    totalSignals,
    newSignals,
    highProximity,
    personasWithLinks,
    favourable,
    unfavourable,
    lastRefreshed: marketSummary?.generated_at || null,
  };
}

export function allSummaryCountsZero(summary) {
  return (
    summary.totalSignals === 0 &&
    summary.highProximity === 0 &&
    summary.personasWithLinks === 0 &&
    summary.favourable === 0 &&
    summary.unfavourable === 0
  );
}

export function isMarketSummaryUnavailable(marketSummary) {
  return marketSummary?.unavailable === true;
}

export function marketSummaryBodyText(marketSummary) {
  const text = String(marketSummary?.summary || '').trim();
  if (text) return text;
  if (isMarketSummaryUnavailable(marketSummary)) {
    return "Summary couldn't be generated just now — the activity below is current.";
  }
  return 'No competitor activity logged this window.';
}

export function isCompetitorMarketEmpty(data, summary) {
  if (!data) return true;
  const competitorRows = data.competitorView?.rows || [];
  const accountRows = data.accountBarometer?.rows || [];
  const personaRows = data.personaBarometer?.rows || [];
  const kpiVisible = summary && !allSummaryCountsZero(summary);
  const summaryVisible = Boolean(data.marketSummary?.summary);
  return (
    !kpiVisible &&
    !summaryVisible &&
    competitorRows.length === 0 &&
    accountRows.length === 0 &&
    personaRows.length === 0
  );
}

/** Parse Signals page signal_focus URL param. */
export function parseSignalFocusParam(raw) {
  if (raw === 'competitor' || raw === 'account_persona') return 'account_persona';
  if (raw === 'market') return 'market';
  return 'all';
}

export function formatUpdateType(type) {
  if (!type) return '';
  return String(type)
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function competitorHasTargetSignals(row) {
  return (Number(row?.accounts_affected) || 0) > 0 || (Number(row?.personas_affected) || 0) > 0;
}

export function filtersToParams({ country, competitor }) {
  const params = countryFilterToParams(country);
  if (competitor && String(competitor).trim()) params.competitor = String(competitor).trim();
  return params;
}
